import { getFileDownloadUrlWithNewName } from "apis/documents";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import React, { useState } from "react";
import { TaskCompletedDocuments } from "types/Models/task";
import { FileIcon } from "components/design/FileIcon";
import { Preview } from "components/PreviewModal";

type FilingReviewSectionDisplayProps = {
  sectionTitle: string;
  sectionFields: any;
  repeatable_fields: any;
};

type SectionFieldValue =
  | string
  | number
  | boolean
  | React.ReactElement<any, string | React.JSXElementConstructor<any>>
  | Iterable<React.ReactNode>
  | null
  | undefined;

export type SectionFieldsFieldProps = {
  type: string;
  value: SectionFieldValue;
  field_label: any;
  documents?: TaskCompletedDocuments[];
};

export const FilingInfo = ({
  label,
  value,
}: {
  label: string;
  value?: SectionFieldValue;
}) => {
  return (
    <div className="t-space-y-1">
      <div
        className="t-text-text-30 t-text-body-sm"
        dangerouslySetInnerHTML={{
          __html: label,
        }}
      ></div>
      <div className="t-text-text-100 t-text-subtext">{value || "-"}</div>
    </div>
  );
};

export const DocumentInfo = ({ file }: { file: TaskCompletedDocuments }) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  return (
    <>
      <span
        key={file.uuid}
        className="t-flex t-gap-2 t-cursor-pointer"
        onClick={() => setPreviewFileId(file?.uuid)}
      >
        <FileIcon fileType={file?.file_type} size="20" />
        <Button customType="link">{file.name}</Button>
      </span>
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={() => setPreviewFileId(null)}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};

export const FilingReviewSectionDisplay = ({
  sectionTitle,
  sectionFields,
  repeatable_fields,
}: FilingReviewSectionDisplayProps) => {
  const checkBox = "checkbox";
  const selectAutofill = "select_autofill";
  const uploadedFiles: TaskCompletedDocuments[] =
    sectionFields?.find(
      (field: SectionFieldsFieldProps) => field.type === "file_upload"
    )?.documents || [];

  return (
    <div className="t-border t-rounded-lg t-border-solid t-border-neutral-10 t-p-6 t-pb-0">
      <div className="t-border t-border-solid t-border-t-0 t-border-l-0 t-border-r-0 t-border-neutral-10 t-w-full t-text-text-100 t-text-subtext t-pb-3 t-mb-4">
        {sectionTitle}
      </div>
      <div className="t-mb-6">
        <div className="t-grid t-gap-x-20 t-gap-y-6 t-grid-cols-2">
          {sectionFields?.map(
            (
              field: SectionFieldsFieldProps,
              index: React.Key | null | undefined
            ) => {
              if (field.type === checkBox) {
                return (
                  <ToolTip
                    key={index}
                    text="This cannot be changed in review information"
                  >
                    <div className="t-col-span-2">
                      <Checkbox
                        label={
                          <div className="t-text-text-30 t-text-body-sm t-mt-1">
                            {field.field_label}
                          </div>
                        }
                        checked={field.value ? true : false}
                        disabled
                        alignTop
                      />
                    </div>
                  </ToolTip>
                );
              }

              if (field.type === "file_upload") {
                return (
                  <FilingInfo
                    key={index}
                    label={field.field_label}
                    value={
                      field.documents && field.documents?.length > 0
                        ? field.documents
                            .map((file: any) => file.name)
                            .join(", ")
                        : "-"
                    }
                  />
                );
              }

              if (
                field.type === selectAutofill ||
                field.type === "select_authorized_signatory_autofill" ||
                field.type === "select_principal_officer_autofill" ||
                !field.field_label
              ) {
                return null;
              }

              return (
                <FilingInfo
                  key={index}
                  label={field.field_label}
                  value={
                    Array.isArray(field.value)
                      ? field.value.join(", ")
                      : field.value
                  }
                />
              );
            }
          )}
        </div>
        <div className="t-space-y-6 t-mt-4">
          {repeatable_fields?.map(
            (repeatableField: { fields: any[]; id: number }, index: number) => {
              return (
                <div
                  key={repeatableField.id}
                  className="t-border t-rounded-lg t-border-solid t-border-neutral-10 t-p-4"
                >
                  <div className="t-text-text-100 t-text-subtext t-mb-4">
                    {sectionTitle} {index + 1}
                  </div>
                  <div className="t-grid t-gap-x-20 t-gap-y-6 t-grid-cols-2">
                    {repeatableField.fields?.map((field, index) => {
                      if (field.type === selectAutofill) {
                        return null;
                      }

                      if (field.type === checkBox) {
                        return (
                          <ToolTip
                            key={index}
                            text="This cannot be changed in review information"
                          >
                            <div className="t-col-span-2">
                              <Checkbox
                                label={
                                  <div className="t-text-text-30 t-text-body-sm t-mt-1">
                                    {field.field_label}
                                  </div>
                                }
                                checked={field.value ? true : false}
                                disabled
                                alignTop
                              />
                            </div>
                          </ToolTip>
                        );
                      }

                      if (field.type === "file_upload") {
                        return (
                          <FilingInfo
                            key={index}
                            label={field.field_label}
                            value={
                              field.documents?.length > 0
                                ? field.documents
                                    .map((file: any) => file.name)
                                    .join(", ")
                                : "-"
                            }
                          />
                        );
                      }

                      return (
                        <FilingInfo
                          key={index}
                          label={field.field_label}
                          value={field.value}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
      {uploadedFiles.length > 0 && (
        <div className="t-space-y-1 t-mb-6">
          <div className="t-text-text-30 t-text-body-sm">Documents</div>
          <div className="t-space-y-2 t-flex t-flex-col">
            {uploadedFiles.map((file) => (
              <DocumentInfo key={file.uuid} file={file} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
